import { batch } from 'react-redux';
import { createSpan, endSpan } from 'src/common/helpers/tracing-helper';
import XLRLogger from 'src/common/helpers/logger';
import { CartStatus } from 'src/items-list/business-logic/service/enumerations/cart-status';
import { CartResponse } from 'src/items-list/business-logic/service/interfaces/cart-response';
import { popupMessage } from 'src/messages/business-logic/popup-message';
import { getTouchPointNotifications } from 'src/touchpoint-notifications/get-touchpoint-notifications';
import { EJRecordTypes } from '../../common/enumerations/ej-record-types';
import { store } from '../../common/redux/configure-store';
import { TenderingActions } from '../../common/redux/reducers/tendering-reducer';
import { openCashDrawer } from '../../devices/cash-drawer-operations';
import { printReceipts } from '../../devices/printer-operations';
import { withSpinner } from 'src/common/components/with-spinner/with-spinner';
import {
  setCashback,
  setChangeDue,
  showThankYou,
  showUnusedCouponDialog,
} from '../../tendering/business-logic/tendering-actions';
import { resetCardData } from 'src/devices/payment/get-card-details';
import {
  inPaymentSession,
  endPaymentSession,
  cancelPinpadOperation,
} from 'src/devices/payment/pinpad-operations';
import { calculateChange } from './calculate-change';
import { ItemListActions } from 'src/common/redux/reducers/item-list-reducer';

export const completeTransaction = async (
  transaction: CartResponse,
  isPrintReceipt: boolean = true,
): Promise<void> => {
  if (!isCartFinalized(transaction.cartInfo?.status)) {
    return;
  }

  batch(
    async (): Promise<void> => {
      const span = createSpan('complete-transaction');
      const change = calculateChange(transaction);
      setCashback(transaction.cartInfo?.totals?.cashbackAmount ?? 0);
      setChangeDue(change);
      store.dispatch(ItemListActions.setPayments(transaction.payments || []));

      const ewicReceipt = store.getState().tendering.ewicReceipts;
      if (ewicReceipt && transaction.receipts) {
        transaction.receipts = [...transaction.receipts, ...ewicReceipt];
      }
      if (transaction.receipts && isPrintReceipt) {
        await printReceipts(transaction.receipts).catch(popupMessage);
      }

      let drawerOpen = false;
      if (transaction.identifiers?.openTillDrawer && !store.getState().posMode.trainingMode) {
        drawerOpen = await openCashDrawer();
      }
      store.dispatch(TenderingActions.setWaitForDrawerClose(drawerOpen));

      const containsUnusedCoupon = store
        .getState()
        .itemList.ejItems.filter(
          i => i.Type === EJRecordTypes.Coupon && !i.Voided && i.CouponRedemptionState?.Unused,
        );
      if (containsUnusedCoupon.length) {
        await showUnusedCouponDialog();
      }

      if (store.getState().pinPad.isPinpadConfigured) {
        if (inPaymentSession()) {
          await withSpinner(async () => {
            await cancelPinpadOperation()
              .then(endPaymentSession)
              .catch(err => {
                XLRLogger.info({
                  message: 'Error cancelling request on PIN Pad',
                  source: 'complete-transaction.ts',
                  data: err,
                });
              });
          });
        }
        resetCardData();
      }

      showThankYou(!!isPrintReceipt, transaction.identifiers?.openTillDrawer ?? drawerOpen);

      await getTouchPointNotifications();
      endSpan(span);
    },
  );
};

export const isCartFinalized = (cartStatus?: CartStatus): boolean => {
  switch (cartStatus) {
    // SE return Finalization
    case CartStatus.Finalization:
      return true;
    case undefined:
      // R10 return undefined
      return true;
    default:
      return false;
  }
};
